/**
 * Turns the JSON the editing system writes into domain blocks.
 *
 * This is the anti-corruption layer between the CMS and the rest of the
 * application. The shape on disk is the CMS's business - nested under locale
 * keys, structural fields duplicated per language, blank strings where an
 * editor cleared a field. None of that is allowed past this file.
 *
 * Everything here is defensive on purpose. The input is text a person typed
 * into a form, so it can be wrong in every way text can be wrong, and it can be
 * wrong *after* deployment. A malformed section is therefore dropped with a
 * warning rather than thrown: a missing section is a bad afternoon, a white
 * screen on a patient organisation's website is worse.
 *
 * Also loaded by scripts/prerender.js at build time, so no package imports -
 * relative ones are resolved by that loader.
 */

import {
  Block,
  BlockAlign,
  BlockLink,
  BlockTone,
  BlockWidth,
  BLOCK_ALIGNS,
  BLOCK_PLACEMENT_END,
  BLOCK_TONES,
  BLOCK_WIDTHS,
} from '../../../corelogic/model/blocks';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A non-empty trimmed string, or undefined. Blank CMS fields count as absent. */
const str = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const strList = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(str).filter((entry): entry is string => entry !== undefined) : [];

const oneOf = <T extends string>(value: unknown, allowed: T[], fallback: T): T =>
  typeof value === 'string' && (allowed as string[]).includes(value) ? (value as T) : fallback;

const link = (value: unknown): BlockLink | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const label = str(value.label);
  const url = str(value.url);
  // Half a link is not a link - a button with no target is a dead end.
  return label && url ? { label, url } : undefined;
};

const objectList = <T>(value: unknown, map: (entry: Record<string, unknown>) => T | undefined): T[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter(isRecord)
    .map(map)
    .filter((entry): entry is T => entry !== undefined);
};

/**
 * A readable name for a document whose label an editor left blank.
 *
 * Worth the fallback rather than dropping the entry: the label is translated
 * and the file is not, so an untranslated label would make a document vanish
 * from the English page while it sits on the German one. A file name is poor
 * link text; a missing document is worse.
 */
const nameFromFile = (file: string): string => {
  const base = file.split(/[?#]/)[0].split('/').pop() ?? '';
  let decoded = base;
  try {
    decoded = decodeURIComponent(base);
  } catch {
    // A stray percent sign in a file name is not worth failing over.
  }
  const name = decoded
    .replace(/\.[a-z0-9]{1,8}$/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return name === '' ? file : name;
};

const warn = (message: string) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[blocks] ${message}`);
  }
};

/**
 * Turns one raw CMS entry into a block, or undefined if it cannot carry its
 * own weight - a text block with no text is not worth a section of whitespace.
 */
const toBlock = (raw: unknown): Block | undefined => {
  if (!isRecord(raw)) {
    return undefined;
  }

  const tone = oneOf<BlockTone>(raw.tone, BLOCK_TONES, 'default');
  const anchor = str(raw.anchor);
  // A placement naming a section that no longer exists is not validated here -
  // the page decides, because only it knows its own sections.
  const placement = str(raw.placement) ?? BLOCK_PLACEMENT_END;
  const base = { tone, placement, ...(anchor ? { anchor } : {}) };
  const width = () => oneOf<BlockWidth>(raw.width, BLOCK_WIDTHS, 'xl');
  const align = () => oneOf<BlockAlign>(raw.align, BLOCK_ALIGNS, 'left');

  switch (raw.type) {
    case 'text': {
      const paragraphs = strList(raw.paragraphs);
      const title = str(raw.title);
      if (paragraphs.length === 0 && !title) {
        warn('dropped an empty text block');
        return undefined;
      }
      return {
        ...base,
        type: 'text',
        eyebrow: str(raw.eyebrow),
        title,
        paragraphs,
        width: width(),
        align: align(),
      };
    }

    case 'cards': {
      const items = objectList(raw.items, (entry) => {
        const title = str(entry.title);
        return title ? { title, description: str(entry.description) ?? '' } : undefined;
      });
      if (items.length === 0) {
        warn('dropped a cards block without usable cards');
        return undefined;
      }
      return {
        ...base,
        type: 'cards',
        title: str(raw.title),
        description: str(raw.description),
        items,
        align: align(),
      };
    }

    case 'list': {
      const items = strList(raw.items);
      if (items.length === 0) {
        warn('dropped a list block without entries');
        return undefined;
      }
      return {
        ...base,
        type: 'list',
        title: str(raw.title),
        description: str(raw.description),
        items,
        width: width(),
      };
    }

    case 'teasers': {
      const items = objectList(raw.items, (entry) => {
        const title = str(entry.title);
        return title
          ? { title, description: str(entry.description) ?? '', link: link(entry.link) }
          : undefined;
      });
      if (items.length === 0) {
        warn('dropped a teasers block without entries');
        return undefined;
      }
      return { ...base, type: 'teasers', title: str(raw.title), items };
    }

    case 'cta': {
      const title = str(raw.title);
      const target = link(raw.link);
      // A heading or a button - either makes a section. Neither makes nothing:
      // a block with only a line of text in it says nothing a text block does
      // not say better, and is more often a half-filled form than an intention.
      if (!title && !target) {
        warn('dropped a call-to-action block with neither a heading nor a button');
        return undefined;
      }
      // No tone of its own any more. The site runs on one background from top
      // to bottom, so a block that arrived CI-blue unless someone changed it
      // would put the colour back one added section at a time. The choice is
      // still offered - it is now a decision rather than the default.
      return {
        ...base,
        type: 'cta',
        title,
        description: str(raw.description),
        link: target,
      };
    }

    case 'quote': {
      const text = str(raw.text);
      if (!text) {
        warn('dropped an empty quote block');
        return undefined;
      }
      return { ...base, type: 'quote', text, source: str(raw.source), width: width() };
    }

    case 'image': {
      const src = str(raw.src);
      const alt = str(raw.alt);
      if (!src) {
        warn('dropped an image block without a file');
        return undefined;
      }
      if (alt === undefined) {
        // Not fatal, but it is an accessibility defect worth surfacing.
        warn(`image block "${src}" has no alt text`);
      }
      // The site's Content-Security-Policy only permits images from its own
      // origin. A hotlinked one works in local development, where there is no
      // CSP, and is silently blocked in production - so it has to be called
      // out here rather than discovered by a visitor.
      if (/^https?:/i.test(src)) {
        warn(
          `image block "${src}" points at another site. The Content-Security-Policy ` +
            'blocks that in production - upload the image in the CMS instead, so it is ' +
            'served from this site.'
        );
      }
      return {
        ...base,
        type: 'image',
        src,
        alt: alt ?? '',
        caption: str(raw.caption),
        width: width(),
      };
    }

    case 'downloads': {
      const items = objectList(raw.items, (entry) => {
        const file = str(entry.file);
        if (!file) {
          // A row with a caption but no document is a link to nowhere.
          return undefined;
        }
        const label = str(entry.label);
        if (!label) {
          warn(`download "${file}" has no label - showing the file name instead`);
        }
        return {
          file,
          label: label ?? nameFromFile(file),
          description: str(entry.description),
        };
      });
      if (items.length === 0) {
        warn('dropped a downloads block without files');
        return undefined;
      }
      return {
        ...base,
        type: 'downloads',
        title: str(raw.title),
        description: str(raw.description),
        items,
        width: width(),
      };
    }

    case 'links': {
      const items = objectList(raw.items, (entry) => {
        const url = str(entry.url);
        if (!url) {
          // A caption with no target is a link to nowhere.
          return undefined;
        }
        // Unlike a document, a link has no file name to fall back on, so an
        // entry without a label cannot be shown at all.
        const label = str(entry.label);
        if (!label) {
          warn(`link "${url}" has no label - dropped`);
          return undefined;
        }
        return { label, url, description: str(entry.description) };
      });
      if (items.length === 0) {
        warn('dropped a links block without usable links');
        return undefined;
      }
      return {
        ...base,
        type: 'links',
        title: str(raw.title),
        description: str(raw.description),
        items,
        width: width(),
      };
    }

    case 'faq': {
      const items = objectList(raw.items, (entry) => {
        const question = str(entry.question);
        const answer = str(entry.answer);
        return question && answer ? { question, answer } : undefined;
      });
      if (items.length === 0) {
        warn('dropped an FAQ block without complete question/answer pairs');
        return undefined;
      }
      return {
        ...base,
        type: 'faq',
        title: str(raw.title),
        description: str(raw.description),
        items,
        width: width(),
      };
    }

    case 'topics': {
      const items = objectList(raw.items, (entry) => {
        const title = str(entry.title);
        if (!title) {
          // The title is the tile - without it there is nothing to click on.
          return undefined;
        }
        return {
          title,
          icon: str(entry.icon),
          summary: str(entry.summary),
          text: str(entry.text),
          link: link(entry.link),
        };
      });
      if (items.length === 0) {
        warn('dropped a topics block without usable tiles');
        return undefined;
      }
      return {
        ...base,
        type: 'topics',
        title: str(raw.title),
        description: str(raw.description),
        items,
        width: width(),
        compact: raw.compact === true,
      };
    }

    default:
      warn(`unknown block type "${String(raw.type)}"`);
      return undefined;
  }
};

/**
 * Fills the gaps in a translation from the leading language, position by
 * position: whatever the translator actually wrote wins, everything they left
 * alone comes from the German - including the structural fields the CMS never
 * writes into a translation in the first place.
 *
 * Without this a translated section arrives as words with no `type` and is not
 * a section at all, so it would simply be missing from the English page. That
 * is not hypothetical: the "Was ist M.E.?" section on the start page was
 * translated, saved, and rendered in German only.
 */
const withDefaults = (value: unknown, base: unknown): unknown => {
  if (value === '' || value === undefined || value === null) {
    return base;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return base;
    }
    if (!Array.isArray(base)) {
      return value;
    }
    return value.map((entry, index) => withDefaults(entry, base[index]));
  }

  if (isRecord(value)) {
    if (!isRecord(base)) {
      return value;
    }
    const merged: Record<string, unknown> = { ...base };
    Object.entries(value).forEach(([key, entry]) => {
      merged[key] = withDefaults(entry, base[key]);
    });
    return merged;
  }

  return value;
};

/**
 * Validates a raw `blocks` array from the CMS. Never throws.
 *
 * `base` is the same list in the leading language, passed when `raw` is a
 * translation of it. Every page that carries editor-written sections needs it,
 * whether the page itself was built in the CMS or in code.
 */
export const toBlocks = (raw: unknown, base?: unknown): Block[] => {
  const merged = base === undefined ? raw : withDefaults(raw, base);

  if (merged === undefined || merged === null) {
    return [];
  }
  if (!Array.isArray(merged)) {
    warn('expected a list of blocks');
    return [];
  }
  return merged.map(toBlock).filter((block): block is Block => block !== undefined);
};
